import React, { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../services/apiClient';
import { clientService } from '../../services/clientService';
import { typesService } from '../../services/typesService';
import config from '../../config/env';
import { CopyUrlButton } from './CopyUrlButton';
import { useI18n } from '../../contexts/I18nContext';
import './ScheduleUpdateModal.css';

interface ScheduleUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onFirmwareCreated: () => void;
}

interface FirmwareFormData {
  model: string;
  type: string;
  version: string;
  file_url: string;
  from_version: string;
  file_size: string;
  checksum: string;
  changelog: string;
  is_active: boolean;
  client_id: string;
}

interface ClientOption {
  id: string;
  name: string;
}

interface FormErrors {
  [key: string]: string;
}

interface FirmwareUploadResponse {
  file_url: string;
  filename: string;
  file_size: number;
  file_sha256: string;
  file_md5: string;
  is_valid_esp32: boolean;
  magic: number;
  segments: number;
  flash_mode: string;
  flash_size: string;
  flash_freq: string;
  entry_point: string;
  app_version: string | null;
  project_name: string | null;
  compile_time: string | null;
  compile_date: string | null;
  idf_version: string | null;
  elf_sha256: string | null;
  suggested_version: string | null;
  suggested_model: string | null;
}

interface ModelOption {
  code: string;
  name: string;
  hardwareType: string;
}

const COMPONENT_TYPES = ['MAIN', 'WWWDATA', 'GSM'];

export const ScheduleUpdateModal: React.FC<ScheduleUpdateModalProps> = ({
  isOpen,
  onClose,
  onFirmwareCreated
}) => {
  const { t } = useI18n();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [uploadResult, setUploadResult] = useState<FirmwareUploadResponse | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [formData, setFormData] = useState<FirmwareFormData>({
    model: '',
    type: 'MAIN',
    version: '',
    file_url: '',
    from_version: '',
    file_size: '',
    checksum: '',
    changelog: '',
    is_active: true,
    client_id: ''
  });

  const axiosInstance = useMemo(() => apiClient.getAxiosInstance(), []);

  // Fetch model references from hardware types
  useEffect(() => {
    const fetchModelReferences = async () => {
      try {
        // Get all hardware types
        const hardwareTypes = await typesService.getHardwareTypes(false);

        // Fetch model references for each hardware type
        const allModels: ModelOption[] = [];
        for (const hwType of hardwareTypes) {
          const modelRefs = await typesService.getModelReferences(hwType.id, false);
          for (const ref of modelRefs) {
            allModels.push({
              code: ref.code,
              name: ref.name || ref.code,
              hardwareType: hwType.code
            });
          }
        }

        setModelOptions(allModels);

        // Default to the first model if the form doesn't already have one.
        // The functional setter lets us check the latest value without
        // needing `formData.model` in the effect deps (which would cause
        // unnecessary refetches on every user edit).
        if (allModels.length > 0) {
          setFormData(prev => (prev.model ? prev : { ...prev, model: allModels[0].code }));
        }
      } catch (err) {
        console.warn('[SCHEDULE UPDATE] Could not fetch model references');
      }
    };

    if (isOpen) {
      fetchModelReferences();
    }
  }, [isOpen]);

  // Fetch clients for dropdown
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientList = await clientService.fetchActiveClients();
        setClients(clientList);
      } catch (err) {
        console.warn('[SCHEDULE UPDATE] Could not fetch clients');
      }
    };

    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  // Handle firmware file upload
  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post<FirmwareUploadResponse>(
        '/v2/firmware/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setUploadResult(response.data);

      // Build firmware URL using environment-specific backend base URL (HTTP)
      const baseUrl = config.api.firmwareBaseUrl.replace(/\/$/, '');
      const firmwareUrl = `${baseUrl}/v2/firmware/files/${response.data.filename}`;

      // Auto-fill form fields from upload response
      setFormData(prev => ({
        ...prev,
        version: response.data.suggested_version || prev.version,
        file_url: firmwareUrl,
        file_size: String(response.data.file_size),
        checksum: response.data.file_sha256
      }));

    } catch (err: any) {
      console.error('[SCHEDULE UPDATE] Upload failed:', err);
      let errorMessage = 'Failed to upload firmware file';
      if (err.response?.data?.detail) {
        errorMessage = typeof err.response.data.detail === 'string'
          ? err.response.data.detail
          : JSON.stringify(err.response.data.detail);
      }
      setError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.model.trim()) {
      newErrors.model = 'Device model is required';
    }

    if (!formData.type.trim()) {
      newErrors.type = 'Component type is required';
    }

    if (!formData.version.trim()) {
      newErrors.version = 'Version is required';
    } else if (formData.version.length > 50) {
      newErrors.version = 'Version must not exceed 50 characters';
    }

    if (!formData.file_url.trim()) {
      newErrors.file_url = 'File URL is required';
    } else if (!formData.file_url.startsWith('http://') && !formData.file_url.startsWith('https://')) {
      newErrors.file_url = 'File URL must start with http:// or https://';
    }

    if (formData.file_size && isNaN(parseInt(formData.file_size))) {
      newErrors.file_size = 'File size must be a number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload: any = {
        model: formData.model.trim(),
        type: formData.type.trim(),
        version: formData.version.trim(),
        file_url: formData.file_url.trim(),
        is_active: formData.is_active
      };

      // Add optional fields if provided
      if (formData.from_version.trim()) {
        payload.from_version = formData.from_version.trim();
      }
      if (formData.file_size.trim()) {
        payload.file_size = parseInt(formData.file_size);
      }
      if (formData.checksum.trim()) {
        payload.checksum = formData.checksum.trim();
      }
      if (formData.changelog.trim()) {
        payload.changelog = formData.changelog.trim();
      }
      if (formData.client_id) {
        payload.client_id = formData.client_id;
      }

      await axiosInstance.post('/v2/firmware', payload);

      // Reset form
      setFormData({
        model: '',
        type: 'MAIN',
        version: '',
        file_url: '',
        from_version: '',
        file_size: '',
        checksum: '',
        changelog: '',
        is_active: true,
        client_id: ''
      });
      setErrors({});

      // Notify parent and close
      onFirmwareCreated();
      onClose();
    } catch (err: any) {
      console.error('[SCHEDULE UPDATE] Error:', err);

      let errorMessage = 'Failed to create firmware entry';
      if (err.response?.data?.detail) {
        if (typeof err.response.data.detail === 'string') {
          errorMessage = err.response.data.detail;
        } else if (Array.isArray(err.response.data.detail)) {
          errorMessage = err.response.data.detail.map((e: any) => e.msg).join(', ');
        }
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading && !isUploading) {
      setFormData({
        model: '',
        type: 'MAIN',
        version: '',
        file_url: '',
        from_version: '',
        file_size: '',
        checksum: '',
        changelog: '',
        is_active: true,
        client_id: ''
      });
      setErrors({});
      setError(null);
      setUploadResult(null);
      onClose();
    }
  };

  const handleChange = (field: keyof FirmwareFormData, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="schedule-update-modal schedule-update-modal-v2" data-testid="modal-add-firmware" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-title-section">
            <h2>{t('otaUpdates.addFirmwareVersion')}</h2>
          </div>
          <button className="close-button" data-testid="btn-modal-close" onClick={handleClose} aria-label={t('common.close')}>×</button>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}>×</button>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="modal-content device-sections">
            {/* Section 1: Firmware File Upload */}
            <div className="device-section">
              <h3 className="section-title">{t('otaUpdates.uploadFirmwareFile')}</h3>
              <div className="firmware-upload-area">
                <input
                  type="file"
                  data-testid="field-firmware-file"
                  accept=".bin"
                  onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])}
                  disabled={isLoading || isUploading}
                  className="file-input"
                />
                {isUploading && <p className="upload-status">Uploading and analyzing firmware...</p>}
              </div>

              {uploadResult && (
                <div className="firmware-analysis-result">
                  <h4 className="analysis-title">{t('otaUpdates.extractedFirmwareDetails')}</h4>
                  <div className="analysis-grid">
                    <div className="analysis-item">
                      <span className="analysis-label">Valid ESP32</span>
                      <span className="analysis-value">{uploadResult.is_valid_esp32 ? 'Yes' : 'No'}</span>
                    </div>
                    <div className="analysis-item">
                      <span className="analysis-label">{t('otaUpdates.appVersion')}</span>
                      <span className="analysis-value">{uploadResult.app_version || '-'}</span>
                    </div>
                    <div className="analysis-item">
                      <span className="analysis-label">Project</span>
                      <span className="analysis-value">{uploadResult.project_name || '-'}</span>
                    </div>
                    <div className="analysis-item">
                      <span className="analysis-label">{t('otaUpdates.idfVersion')}</span>
                      <span className="analysis-value">{uploadResult.idf_version || '-'}</span>
                    </div>
                    <div className="analysis-item">
                      <span className="analysis-label">{t('otaUpdates.compileDate')}</span>
                      <span className="analysis-value">{uploadResult.compile_date || '-'}</span>
                    </div>
                    <div className="analysis-item">
                      <span className="analysis-label">{t('otaUpdates.compileTime')}</span>
                      <span className="analysis-value">{uploadResult.compile_time || '-'}</span>
                    </div>
                    <div className="analysis-item">
                      <span className="analysis-label">{t('otaUpdates.flashMode')}</span>
                      <span className="analysis-value">{uploadResult.flash_mode}</span>
                    </div>
                    <div className="analysis-item">
                      <span className="analysis-label">{t('otaUpdates.fileSize')}</span>
                      <span className="analysis-value">{(uploadResult.file_size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                  </div>
                  <div className="analysis-item analysis-sha256">
                    <span className="analysis-label">SHA256</span>
                    <span className="analysis-value">{uploadResult.file_sha256}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Section 2: Firmware Information */}
            <div className="device-section">
              <h3 className="section-title">{t('otaUpdates.firmwareInformation')}</h3>
              <div className="firmware-fields">
                <div className="form-field">
                  <label className="required">{t('otaUpdates.deviceModel')}</label>
                  <select
                    value={formData.model}
                    data-testid="field-firmware-device-model"
                    onChange={(e) => handleChange('model', e.target.value)}
                    disabled={isLoading || modelOptions.length === 0}
                    className={`field-select ${errors.model ? 'error' : ''}`}
                  >
                    {modelOptions.length === 0 ? (
                      <option value="">Loading models...</option>
                    ) : (
                      modelOptions.map(model => (
                        <option key={model.code} value={model.code}>
                          {model.code}{model.name !== model.code ? ` - ${model.name}` : ''} ({model.hardwareType})
                        </option>
                      ))
                    )}
                  </select>
                  {errors.model && <span className="field-error">{errors.model}</span>}
                </div>

                <div className="form-field">
                  <label className="required">{t('otaUpdates.componentType')}</label>
                  <select
                    value={formData.type}
                    data-testid="field-firmware-component-type"
                    onChange={(e) => handleChange('type', e.target.value)}
                    disabled={isLoading}
                    className={`field-select ${errors.type ? 'error' : ''}`}
                  >
                    {COMPONENT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.type && <span className="field-error">{errors.type}</span>}
                </div>

                <div className="form-field">
                  <label className="required">{t('otaUpdates.version')}</label>
                  <input
                    type="text"
                    data-testid="field-firmware-version"
                    value={formData.version}
                    onChange={(e) => handleChange('version', e.target.value)}
                    placeholder="e.g., PGA-00-02-B"
                    disabled={isLoading}
                    className={`field-input ${errors.version ? 'error' : ''}`}
                    maxLength={50}
                  />
                  {errors.version && <span className="field-error">{errors.version}</span>}
                </div>

                <div className="form-field">
                  <label>{t('otaUpdates.fromVersion')}</label>
                  <input
                    type="text"
                    value={formData.from_version}
                    onChange={(e) => handleChange('from_version', e.target.value)}
                    placeholder="e.g., PGA-00-01-A (optional)"
                    disabled={isLoading}
                    className="field-input"
                    maxLength={50}
                  />
                  <span className="field-hint">{t('otaUpdates.leaveEmptyUpgradeAny')}</span>
                </div>
              </div>
            </div>

            {/* Section 3: File Details */}
            <div className="device-section">
              <h3 className="section-title">{t('otaUpdates.fileDetails')}</h3>
              <div className="file-details-fields">
                <div className="form-field form-field-full">
                  <label className="required">{t('otaUpdates.fileUrl')}</label>
                  <div className="url-input-group">
                    <input
                      type="text"
                      data-testid="field-firmware-file-url"
                      value={formData.file_url}
                      onChange={(e) => handleChange('file_url', e.target.value)}
                      placeholder="http://provisioning-test.in.strong.blue/v2/firmware/..."
                      disabled={isLoading}
                      className={`field-input ${errors.file_url ? 'error' : ''}`}
                      maxLength={500}
                    />
                    {formData.file_url && <CopyUrlButton url={formData.file_url} />}
                  </div>
                  {errors.file_url && <span className="field-error">{errors.file_url}</span>}
                </div>

                <div className="form-field">
                  <label>File Size (bytes)</label>
                  <input
                    type="text"
                    value={formData.file_size}
                    onChange={(e) => handleChange('file_size', e.target.value)}
                    placeholder="e.g., 2183904"
                    disabled={isLoading}
                    className={`field-input ${errors.file_size ? 'error' : ''}`}
                  />
                  {errors.file_size && <span className="field-error">{errors.file_size}</span>}
                </div>

                <div className="form-field">
                  <label>Checksum (SHA256)</label>
                  <input
                    type="text"
                    value={formData.checksum}
                    onChange={(e) => handleChange('checksum', e.target.value)}
                    placeholder={t('otaUpdates.sha256Placeholder')}
                    disabled={isLoading}
                    className="field-input"
                    maxLength={100}
                  />
                </div>
              </div>
            </div>

            {/* Section 4: Deployment Options */}
            <div className="device-section">
              <h3 className="section-title">{t('otaUpdates.deploymentOptions')}</h3>
              <div className="deployment-fields">
                <div className="form-field">
                  <label>{t('clients.client')}</label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => handleChange('client_id', e.target.value)}
                    disabled={isLoading}
                    className="field-select"
                  >
                    <option value="">Any Client (Global)</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>{client.name}</option>
                    ))}
                  </select>
                  <span className="field-hint">{t('otaUpdates.leaveEmptyAllClients')}</span>
                </div>

                <div className="form-field form-field-full">
                  <label>Changelog / Release Notes</label>
                  <textarea
                    value={formData.changelog}
                    onChange={(e) => handleChange('changelog', e.target.value)}
                    placeholder={t('otaUpdates.changelogPlaceholder')}
                    disabled={isLoading}
                    className="field-textarea"
                    rows={3}
                    maxLength={1000}
                  />
                </div>

                <div className="form-field form-field-full">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      data-testid="field-firmware-is-active"
                      checked={formData.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                      disabled={isLoading}
                    />
                    <span>Active (available for OTA updates)</span>
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-actions">
            <div className="action-group-left">
              <button type="button" onClick={handleClose} className="btn btn-secondary" data-testid="btn-cancel" disabled={isLoading}>
                Cancel
              </button>
            </div>
            <div className="action-group-right">
              <button type="submit" className="btn btn-primary" data-testid="btn-submit" disabled={isLoading || isUploading}>
                {isLoading ? 'Creating...' : 'Add Firmware'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
